import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType";
import SnResizable from "@shared/components/SnResizable";
import usePanelSizes from "@shared/hooks/usePanelSizes";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import {
  CellValueChangedEvent,
  SnGridRef,
} from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

interface RowData {
  KeyField?: string; // 행의 고유 ID
  [key: string]: any;
}

const SN_COS_MAS_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    grid3: React.createRef(),
  });
  const searchRef = useRef<any>(null);
  const { userInfo } = global();
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [gridFocus, setGridFocus] = useState(false);
  let addedarow = false;

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({});
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        setFormData(rtn.formData); // 조회조건 초기값
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));
  //region onSave
  const onSave = async () => {
    let grid1change;
    let grid2change;
    let grid3change;
    try {
      getGridCurrent("grid1")?.stopEditing();
      getGridCurrent("grid2")?.stopEditing();
      getGridCurrent("grid3")?.stopEditing();

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const requiredValuesCheckD =
        getGridCurrent("grid3").requiredValuesCheck();
      if (!requiredValuesCheckD) return false;

      grid1change = getGridCurrent("grid1").getChange();
      grid2change = getGridCurrent("grid2").getChange();
      grid3change = getGridCurrent("grid3").getChange();
      if (
        grid1change.modifiedRowcount === 0 &&
        grid2change.modifiedRowcount === 0 &&
        grid3change.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return;
      }

      const SaveData = {
        cos_elemh: grid1change,
        cos_eleml: grid2change,
        cos_elem_cc: grid3change,
      };
      // 저장 API 호출
      const ok = await fnSave("SN_COS_MAS_REG_001_SAVE_01", SaveData);
      if (!ok) {
        getGridCurrent("grid1").resetUpdate(true);
        getGridCurrent("grid2").resetUpdate(true);
        getGridCurrent("grid3").resetUpdate(true);
        return false;
      }

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      getGridCurrent("grid3").resetUpdate(true);
      return false;
    }
  };

  //region onSearch
  const onSearch = async () => {
    const grid1change = gridRefs.current["grid1"].current.getChange();
    const grid2change = gridRefs.current["grid2"].current.getChange();
    const grid3change = gridRefs.current["grid3"].current.getChange();
    if (
      grid1change.modifiedRowcount > 0 ||
      grid2change.modifiedRowcount > 0 ||
      grid3change.modifiedRowcount
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        await onSave();
      }
    }
    try {
      const params = {
        id: "sn_cos_mas_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      gridRefs.current["grid3"].current.resetUpdate();

      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
      addedarow = false;
    } catch (error: any) {}
  };

  //region onInsert
  const onInsert = async () => {
    try {
      getGridCurrent("grid1")?.addRow();
    } catch (error: any) {}
  };

  //region onBeforeAddRow1
  const onBeforeAddRow1 = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("alter", "수정된 내용이 있습니다.");
        return false;
      }
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const fg_costeditvalue = searchRef.current.getFormdata().fg_cost;

      return {
        cd_firm: userInfo.cd_firm,
        yn_use: "Y",
        fg_elem2: "CC",
        fg_cost: fg_costeditvalue,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  //region onAddRow
  const onAddRow = () => {
    try {
      getGridCurrent("grid1")?.stopEditing();
      getGridCurrent("grid2")?.stopEditing();
      return getGridCurrent("grid2")?.addRow();
    } catch (error: any) {}
  };

  //region onBeforeAddRow2
  const onBeforeAddRow2 = () => {
    try {
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      return {
        cd_firm: userInfo.cd_firm,
        cd_elem: focusedNodeH.data?.cd_elem,
        fg_cost: focusedNodeH.data?.fg_cost,
      };
    } catch (error: any) {
      return false;
    }
  };

  const onGrid3BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow3();
    }
    if (id == "deleteRow") {
      onDeleteRow3();
    }
  };

  //region onAddRow
  const onAddRow3 = () => {
    try {
      getGridCurrent("grid1")?.stopEditing();
      getGridCurrent("grid2")?.stopEditing();
      return getGridCurrent("grid3")?.addRow();
    } catch (error: any) {}
  };

  //region onBeforeAddRow3
  const onBeforeAddRow3 = () => {
    try {
      const focusedNodeH = getGridCurrent("grid1").focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      return {
        cd_firm: userInfo.cd_firm,
        cd_elem: focusedNodeH.data?.cd_elem,
        fg_cost: focusedNodeH.data?.fg_cost,
      };
    } catch (error: any) {
      return false;
    }
  };

  //region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1")?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    getGridCurrent("grid3")?.deleteAllRows();
    getGridCurrent("grid2")?.deleteAllRows();
    getGridCurrent("grid1")?.deleteSelectedRows();
    addedarow = false;

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();
      return;
    }

    return await onSave();
  };

  //region onDeleteRow
  const onDeleteRow = async () => {
    const rowCount = getGridCurrent("grid2")?.getDisplayedRowCount();
    if (rowCount === 0) return;
    getGridCurrent("grid2")?.deleteSelectedRows();
  };

  const onDeleteRow3 = async () => {
    const rowCount = getGridCurrent("grid3")?.getDisplayedRowCount();
    if (rowCount === 0) return;
    getGridCurrent("grid3")?.deleteSelectedRows();
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRow1,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRow2,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
        },
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {
          onBeforeAddRow: onBeforeAddRow3,
          onBtnAddClick: onGrid3BtnsClick,
          onBtnDeleteClick: onGrid3BtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex h-full min-h-0 gap-2">
        <SnResizeBar direction={"width"} id="panel1" orgSize="55.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "요소" }}
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["fg_cost", "cd_elem"],
            }}
            events={grids.grid1.events}
          />
        </SnResizeBar>

        <div className="flex flex-1 flex-col min-h-0 gap-2">
          <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
            <SnGrid
              ref={gridRefs.current["grid2"]}
              columnConfig={grids.grid2.columns}
              rowData={grids.grid2.dataSource}
              etcProps={{
                rowSelection: "multiple",
                title: "계정과목",
              }}
              events={grids.grid2.events}
            />
          </SnResizeBar>

          <SnResizeBar direction={"height"} id="panel3" orgSize="45.5%">
            <SnGrid
              ref={gridRefs.current["grid3"]}
              columnConfig={grids.grid3.columns}
              rowData={grids.grid3.dataSource}
              etcProps={{
                rowSelection: "multiple",
                title: "부문",
              }}
              events={grids.grid3.events}
            />
          </SnResizeBar>
        </div>
      </div>
    </div>
  );
  //#endregion
});

export default SN_COS_MAS_REG_001;
